import React, { useState, useEffect } from 'react';
import config from '../config';
import { getUserIdFromUrl } from '../helper/apiHelper';
import { useTranslation } from '../hooks/useTranslation';
import { useStatic } from '../context/StaticContext';
import { useMaintenanceMode } from '../context/MaintenanceContext'; // Bakım modu hook'u
import SuccessAlert from './alerts/SuccessAlert';
import ErrorAlert from './alerts/ErrorAlert';
import WarningAlert from './alerts/WarningAlert';
import InfoAlert from './alerts/InfoAlert';
import PhoneAccess, { validatePhone } from './PhoneAccess';

const BonusConfirmPopup = ({ isOpen, onClose, bonus, onConfirm, onBonusSuccess, redirectLink, onTelegramPopupOpen }) => {
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [alertData, setAlertData] = useState({
    show: false,
    type: null, // 'success', 'error', 'warning', 'info'
    message: ''
  });
  const { t } = useTranslation();
  const { selectedLanguage, isLanguage } = useStatic();
  const { isMaintenanceMode } = useMaintenanceMode(); // Bakım modu bilgisini al

  // Body scroll lock effect
  useEffect(() => {
    if (isOpen) {
      // Popup açıldığında body scroll'unu kilitle
      document.body.classList.add('body-scroll-lock');
      // iOS Safari için ek optimizasyon
      document.documentElement.style.overflow = 'hidden';
    } else {
      // Popup kapandığında body scroll'unu serbest bırak
      document.body.classList.remove('body-scroll-lock');
      document.documentElement.style.overflow = '';
    }

    // Cleanup function
    return () => {
      document.body.classList.remove('body-scroll-lock');
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  // Dinamik çeviri fonksiyonu (MyMemory API kullanarak)
  const translateMessage = async (text, targetLang) => {
    // isLanguage yoksa veya false ise çevirme yapma
    if (!isLanguage || !targetLang || targetLang === 'tr-TR') {
      return text; // Türkçe ise veya dil desteği kapalıysa çevirme
    }

    try {
      // Dil kodunu API formatına çevir (tr-TR -> tr|en)
      const langMap = {
        'en-US': 'tr|en',
        'de-DE': 'tr|de',
        'fr-FR': 'tr|fr',
        'es-ES': 'tr|es',
        'it-IT': 'tr|it',
        'pt-PT': 'tr|pt'
      };

      const langPair = langMap[targetLang];
      if (!langPair) return text;

      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`
      );

      const data = await response.json();

      if (data.responseStatus === 200 && data.responseData.translatedText) {
        return data.responseData.translatedText;
      }

      return text; // Çeviri başarısızsa orijinal metni döndür
    } catch (error) {
      console.warn('Translation failed:', error);
      return text; // Hata durumunda orijinal metni döndür
    }
  };

  if (!isOpen) return null;

  const handleConfirmBonus = async () => {
    // Redirect link varsa Telegram popup'ını aç
    if (redirectLink) {
      onClose(); // Mevcut popup'ı kapat
      onTelegramPopupOpen(); // Telegram popup'ını aç
      return;
    }

    // Telefon zorunluysa önce doğrula
    if (bonus?.required_phone) {
      const { isValid, normalized, error } = validatePhone(phone);
      if (!isValid) {
        setPhoneError(error);
        return;
      }
      setPhoneError('');
    }
    setLoading(true);
    try {
      let payload = {};
      if (bonus.required_phone === true) {
          // İstek payload'ını oluştur
          payload = {
            process_id: bonus.process_name,
            user_id: getUserIdFromUrl(),
            recaptcha: "test",
            data: {
              "phone": phone
            }
          };
      }
      else {
        // İstek payload'ını oluştur
        payload = {
          process_id: bonus.process_name,
          user_id: getUserIdFromUrl(),
          recaptcha: "test",
          data: null
        };
      }

      // Bakım modu kontrolü - BonusContext'teki gibi
      if (isMaintenanceMode) {
        payload.dev = "10";
        console.log('🔧 Bonus talep - Bakım modunda dev=10 parametresi eklendi', payload);
      } else {
        console.log('✅ Bonus talep - Normal modda, dev parametresi eklenmedi', payload);
      }

      let body = JSON.stringify(payload);
      let keys = await window.rh.cr(body);
      const response = await fetch(`${config.apiBaseUrl2}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "hk": keys[0],
          "hh": keys[1]
        },
        body: body,
      });

      const data = await response.json();

      // API yanıtına göre alert türünü ve mesajını belirle
      let messageToTranslate = '';
      let alertType = '';

      if (data.status === true) {
        messageToTranslate = data.message || 'Bonus talebi başarıyla onaylandı!';
        alertType = 'success';
      } else if (data.status === 3) {
        messageToTranslate = data.message || 'Bonus talebiniz için bir uyarı mevcut.';
        alertType = 'warning';
      } else if (data.status === 4) {
        messageToTranslate = data.message || 'Bonus talebiniz hakkında bilgilendirme.';
        alertType = 'info';
      } else {
        messageToTranslate = data.message || 'Bonus talebiniz onaylanamadı.';
        alertType = 'error';
      }

      // Mesajı seçili dile çevir (isLanguage kontrolü translateMessage fonksiyonunda)
      const translatedMessage = await translateMessage(messageToTranslate, selectedLanguage);

      setAlertData({
        show: true,
        type: alertType,
        message: translatedMessage
      });

      // Alert gösterildikten 3 saniye sonra popup'ı kapat
      setTimeout(() => {
        onClose();
        // Her durumda history popup'ı açmak için callback çağır
        if (onBonusSuccess) {
          onBonusSuccess();
        }
        // Alert'i kapat
        setTimeout(() => {
          setAlertData({ show: false, type: null, message: '' });
        }, 500);
      }, 2000);

    } catch (error) {
      console.error('Bonus onayı sırasında hata oluştu:', error);

      // Hata mesajını da çevir
      const errorMessage = 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
      const translatedErrorMessage = await translateMessage(errorMessage, selectedLanguage);

      setAlertData({
        show: true,
        type: 'error',
        message: translatedErrorMessage
      });

      // Hata durumunda da history popup'ı açmak için callback çağır
      setTimeout(() => {
        onClose();
        if (onBonusSuccess) {
          onBonusSuccess();
        }
        // Alert'i kapat
        setTimeout(() => {
          setAlertData({ show: false, type: null, message: '' });
        }, 500);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  // Overlay click handler - sadece overlay'e tıklandığında kapansın
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Alert tipine göre uygun bileşeni göster
  const renderAlert = () => {
    if (!alertData.show) return null;

    switch (alertData.type) {
      case 'success':
        return <SuccessAlert message={alertData.message} />;
      case 'warning':
        return <WarningAlert message={alertData.message} />;
      case 'info':
        return <InfoAlert message={alertData.message} />;
      case 'error':
      default:
        return <ErrorAlert message={alertData.message} />;
    }
  };

  return (
    <div
      className="bonus-confirm-overlay"
      onClick={handleOverlayClick}
      style={{
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease-in-out'
      }}
    >
      {renderAlert()}
      <div
        className="bonus-confirm-popup"
        style={{
          animation: 'modalSlideIn 0.3s ease-out',
        }}
      >
        <div className="bonus-confirm-header">
          <button
            className="close-button"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="bonus-confirm-content">
          <h3>{bonus.title}</h3>

          <div className="bonus-confirm-image-container">
            <img src={bonus.image} alt={bonus.title} className="bonus-confirm-image" />
          </div>

          <p className="bonus-confirm-question" style={{color: "white",fontSize: "18px"}}>
            {t('popup.confirmTitle', 'Bu bonusu talep etmek istediğinizden emin misiniz?')}
          </p>

          {bonus?.required_phone && (
            <PhoneAccess
              value={phone}
              onChange={setPhone}
              error={phoneError}
              onErrorChange={setPhoneError}
              autoFocus
            />
          )}
        </div>

        <div className="bonus-confirm-actions">
          <button
            className="bonus-confirm-button"
            onClick={handleConfirmBonus}
            disabled={loading}
          >
            {loading ? t('buttons.processing', 'İşleniyor...') : t('buttons.request', 'Talep Et')}
          </button>
          <button
            className="bonus-cancel-button"
            onClick={onClose}
            disabled={loading}
          >
            {t('buttons.cancel', 'Vazgeç')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BonusConfirmPopup; 